// Media upload limits — pure, dependency-free, and bundle-safe for the SPA so
// the client can pre-flight a pick with the exact numbers the backend enforces
// (no `process.env` reads at module scope; same convention as
// `shared/walkCapsMath.ts`).
//
// Why an RPC ceiling exists at all: every attach path base64s the file into a
// single WebSocket frame (`useSynced.ts` -> `ws.send(JSON.stringify(...))`), and
// `Bun.serve`'s `websocket.maxPayloadLength` defaults to 16 MiB. Base64 inflates
// by 4/3, so a source file over ~12 MB overflowed the frame, Bun closed the
// socket, and the in-flight RPC rejected — surfacing as "Could not attach
// media" with the optimistic tile rolled back. Guarding here turns that dead
// socket into a diagnosable error. Video does not go through the RPC at all;
// it uses the upload-intent -> presigned PUT -> commit flow.

/** Containers we are willing to store and serve. */
export const ALLOWED_VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
] as const;

export type AllowedVideoMimeType = (typeof ALLOWED_VIDEO_MIME_TYPES)[number];

/** Full-resolution phone JPEG envelope for native multipart uploads. */
export const PHOTO_MAX_BYTES = 24 * 1024 * 1024;

/** 500 MB — agreed envelope, sized for walkthrough-style footage. */
export const VIDEO_MAX_BYTES = 500 * 1024 * 1024;

/** 15 minutes. */
export const VIDEO_MAX_DURATION_MS = 15 * 60 * 1000;

/**
 * Largest decoded payload allowed through a base64 WebSocket RPC. Kept well
 * under the 16 MiB frame cap once inflated by 4/3 (8 MiB -> ~10.7 MiB on the
 * wire), leaving headroom for the JSON envelope around it.
 */
export const RPC_MAX_BASE64_DECODED_BYTES = 8 * 1024 * 1024;

export type MediaLimitFailureCode =
  | "payload_too_large"
  | "video_requires_upload_intent"
  | "unsupported_media_type"
  | "file_too_large"
  | "duration_too_long"
  | "invalid_size";

export type MediaLimitResult =
  | { ok: true }
  | { ok: false; code: MediaLimitFailureCode; message: string };

const OK: MediaLimitResult = { ok: true };

function fail(code: MediaLimitFailureCode, message: string): MediaLimitResult {
  return { ok: false, code, message };
}

/** Decoded byte length of a base64 string, without allocating a Buffer. */
export function base64DecodedByteLength(base64: string): number {
  if (!base64) return 0;
  let padding = 0;
  if (base64.endsWith("==")) padding = 2;
  else if (base64.endsWith("=")) padding = 1;
  return (base64.length / 4) * 3 - padding;
}

export function isAllowedVideoMimeType(mimeType: string): boolean {
  return (ALLOWED_VIDEO_MIME_TYPES as readonly string[]).includes(
    mimeType.toLowerCase(),
  );
}

/**
 * Guard for the legacy base64 RPC attach paths. Videos are always redirected to
 * the upload-intent flow; images are bounded so an oversized pick fails with a
 * real message instead of killing the socket.
 */
export function checkRpcMediaPayload(params: {
  base64: string;
  mimeType: string;
}): MediaLimitResult {
  const bytes = base64DecodedByteLength(params.base64);
  if (bytes <= RPC_MAX_BASE64_DECODED_BYTES) return OK;

  if (params.mimeType.toLowerCase().startsWith("video/")) {
    return fail(
      "video_requires_upload_intent",
      "Videos are too large to attach over this channel. Use the video upload flow instead.",
    );
  }

  return fail(
    "payload_too_large",
    `This file is too large to attach (limit ${Math.floor(
      RPC_MAX_BASE64_DECODED_BYTES / (1024 * 1024),
    )} MB).`,
  );
}

/** Validation for `POST /api/visits/:visitId/media/upload-intent`. */
export function validateVideoUpload(params: {
  mimeType: string;
  byteSize: number;
  durationMs?: number;
}): MediaLimitResult {
  if (!isAllowedVideoMimeType(params.mimeType)) {
    return fail(
      "unsupported_media_type",
      `Unsupported video format. Supported: ${ALLOWED_VIDEO_MIME_TYPES.join(", ")}.`,
    );
  }

  if (!Number.isFinite(params.byteSize) || params.byteSize <= 0) {
    return fail("invalid_size", "Video size is missing or invalid.");
  }

  if (params.byteSize > VIDEO_MAX_BYTES) {
    return fail(
      "file_too_large",
      `Video is too large (limit ${Math.floor(VIDEO_MAX_BYTES / (1024 * 1024))} MB).`,
    );
  }

  // Duration is best-effort: some containers do not expose it to the client
  // before upload, and rejecting on "unknown" would block legitimate files.
  if (
    params.durationMs !== undefined &&
    Number.isFinite(params.durationMs) &&
    params.durationMs > VIDEO_MAX_DURATION_MS
  ) {
    return fail(
      "duration_too_long",
      `Video is too long (limit ${Math.floor(VIDEO_MAX_DURATION_MS / 60000)} minutes).`,
    );
  }

  return OK;
}
